import React, { useContext, useEffect, useState } from "react";
import { useAuthContext } from "../../../contexts/AuthContext";

import Sidebar from "../../UI/Sidebar";
import Loader from "../../UI/Loader";
import ErrorMessage from "../../UI/ErrorMessage";

import { sfQuery } from "../../API";
import { useParams } from "react-router";

const ProfitabilityDashboard = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [cancellations] = useState({});
  const [refunds] = useState({});
  const [credits] = useState({});
  const [error] = useState(false);
  const [showOlder, setShowOlder] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all"); // all, high-value, at-risk, new
  const [sortBy, setSortBy] = useState("amount"); // amount, date, type
  const context = useAuthContext();

  let { owId, sidebar } = useParams();

  const formatCurrency = (value, currencyCode = "USD") => {
    return value !== null && value !== undefined
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currencyCode,
        }).format(value)
      : "";
  };

  // Currency conversion helper - CAD to USD using 1.39 exchange rate
  const convertToUSD = (amount, currencyCode) => {
    if (!amount || currencyCode === "USD") return amount;
    if (currencyCode === "CAD") return amount / 1.39;
    return amount; // Default to no conversion for unknown currencies
  };

  // Format currency with original currency shown next to USD totals
  const formatCurrencyWithOriginal = (value, currencyCode = "USD") => {
    if (!value || currencyCode === "USD") return formatCurrency(value);
    const convertedValue = convertToUSD(value, currencyCode);
    return `${formatCurrency(convertedValue)} (${formatCurrency(
      value,
      currencyCode
    )})`;
  };

  // Enhanced helper functions for better decision making
  const getPriorityLevel = (opportunity) => {
    if (!opportunity) return "standard";
    const mrrAmount = convertToUSD(
      opportunity.Total_MRR__c || 0,
      opportunity.CurrencyIsoCode
    );
    const oneOffAmount = convertToUSD(
      opportunity.Total_One_Offs__c || 0,
      opportunity.CurrencyIsoCode
    );
    const amount = mrrAmount + oneOffAmount;

    if (amount >= 2000) return "legendary";
    if (amount >= 1000) return "amazing";
    if (amount >= 500) return "awesome";
    if (amount >= 200) return "welldone";
    return "standard";
  };

  const getOpportunityAge = (closeDate) => {
    if (!closeDate) return 0;
    const today = new Date();
    const close = new Date(closeDate);
    const diffTime = Math.abs(today - close);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusColor = (item) => {
    if (!item || !item.Opportunity) return "#ffffff";
    const priority = getPriorityLevel(item.Opportunity);
    const age = getOpportunityAge(item.Opportunity.CloseDate);

    if (item.IsPaid) return "#e6f9e6"; // Light green for paid
    if (priority === "high" && age > 30) return "#ffe6e6"; // Light red for high-value, old unpaid
    if (priority === "high") return "#fff3e0"; // Light orange for high-value
    if (age > 60) return "#f5f5f5"; // Gray for very old
    return "#ffffff"; // White default
  };

  const getVisualIndicator = (item) => {
    if (!item || !item.Opportunity) return "👍";
    const mrrAmount = convertToUSD(
      item.Opportunity.Total_MRR__c || 0,
      item.Opportunity.CurrencyIsoCode
    );
    const oneOffAmount = convertToUSD(
      item.Opportunity.Total_One_Offs__c || 0,
      item.Opportunity.CurrencyIsoCode
    );
    const amount = mrrAmount + oneOffAmount;

    // Focus on deal value tiers for Closed Won opportunities
    if (amount >= 2000) return "🏆"; // $2k+ Legendary!!
    if (amount >= 1000) return "🤩"; // $1k+ Amazing
    if (amount >= 500) return "😎"; // $500+ Awesome
    if (amount >= 200) return "👏"; // $200+ Well done
    return "👍"; // Standard deals
  };

  // Function to get tooltip for visual indicators
  const getVisualIndicatorTooltip = (item) => {
    if (!item || !item.Opportunity) return "Standard deal value";
    const mrrAmount = convertToUSD(
      item.Opportunity.Total_MRR__c || 0,
      item.Opportunity.CurrencyIsoCode
    );
    const oneOffAmount = convertToUSD(
      item.Opportunity.Total_One_Offs__c || 0,
      item.Opportunity.CurrencyIsoCode
    );
    const amount = mrrAmount + oneOffAmount;
    const currencyDisplay =
      item.Opportunity.CurrencyIsoCode !== "USD"
        ? ` (${formatCurrency(
            (item.Opportunity.Total_MRR__c || 0) +
              (item.Opportunity.Total_One_Offs__c || 0),
            item.Opportunity.CurrencyIsoCode
          )})`
        : "";

    if (amount >= 2000)
      return `Legendary Deal: ${formatCurrency(
        amount
      )}${currencyDisplay} - Outstanding performance!! 🏆`;
    if (amount >= 1000)
      return `Amazing Deal: ${formatCurrency(
        amount
      )}${currencyDisplay} - Fantastic work! 🤩`;
    if (amount >= 500)
      return `Awesome Deal: ${formatCurrency(
        amount
      )}${currencyDisplay} - Great job! 😎`;
    if (amount >= 200)
      return `Well Done: ${formatCurrency(
        amount
      )}${currencyDisplay} - Nice work! 👏`;
    return `Standard Deal: ${formatCurrency(amount)}${currencyDisplay} 👍`;
  };

  // Function to calculate month-over-month progress
  const getMonthProgress = () => {
    const today = new Date();
    const currentDayOfMonth = today.getDate();
    const daysInCurrentMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    ).getDate();
    const monthProgress = currentDayOfMonth / daysInCurrentMonth;

    // Get current month revenue
    const currentMonthRevenue = [
      ...currentMonthData.FrontEnd,
      ...currentMonthData.BackEnd,
      ...currentMonthData.PricingUpdate,
    ]
      .filter((item) => item?.Opportunity?.Name?.trim())
      .reduce((sum, item) => sum + (item.TotalValue || 0), 0);

    // Get last month revenue
    const lastMonthRevenue = [
      ...lastMonthData.FrontEnd,
      ...lastMonthData.BackEnd,
      ...lastMonthData.PricingUpdate,
    ]
      .filter((item) => item?.Opportunity?.Name?.trim())
      .reduce((sum, item) => sum + (item.TotalValue || 0), 0);

    // Project current month based on progress
    const projectedRevenue = currentMonthRevenue / monthProgress;
    const isOnTrack = projectedRevenue >= lastMonthRevenue;

    return {
      isOnTrack,
      currentMonthRevenue,
      lastMonthRevenue,
      projectedRevenue,
      monthProgress: Math.min(monthProgress * 100, 100),
      currentDayOfMonth,
      daysInCurrentMonth,
    };
  };

  const getActionableInsight = (item) => {
    if (!item || !item.Opportunity) return "Standard follow-up";
    const priority = getPriorityLevel(item.Opportunity);
    const age = getOpportunityAge(item.Opportunity.CloseDate);

    if (item.IsPaid) return "Completed";
    if (priority === "high" && age > 30) return "URGENT: Follow up immediately";
    if (priority === "high") return "High priority - monitor closely";
    if (age > 60) return "Long outstanding - investigate";
    return "Standard follow-up";
  };

  const balanceRows = (rows1, rows2) => {
    const maxLength = Math.max(rows1.length, rows2.length);
    while (rows1.length < maxLength) {
      rows1.push({
        Opportunity: { Name: "", CloseDate: "", Total_MRR__c: 0 },
        IsPaid: false,
        IsProrated: false,
        Priority: "low",
        Age: 0,
        TotalValue: 0,
      });
    }
    while (rows2.length < maxLength) {
      rows2.push({
        Opportunity: { Name: "", CloseDate: "", Total_MRR__c: 0 },
        IsPaid: false,
        IsProrated: false,
        Priority: "low",
        Age: 0,
        TotalValue: 0,
      });
    }
  };

  useEffect(() => {
    if (!context?.ready) return;
    setLoading(true);
    console.log("Starting data fetch for ProfitabilityDashboard...");

    const fetchData = async () => {
      try {
        // Fetch opportunities for profitability analysis
        console.log("Fetching opportunities...");
        const opportunities = await sfQuery(
          "Opportunity",
          "Id, Name, AccountId, RecordType.Name, CloseDate, Total_MRR__c, Total_One_Offs__c, StageName, CurrencyIsoCode",
          `CloseDate >= LAST_N_MONTHS:6 AND StageName = 'Closed Won'`,
          context
        );
        console.log("Opportunities fetched:", opportunities?.length || 0);

        // Fetch orders for payment tracking
        console.log("Fetching orders...");
        const orders = await sfQuery(
          "Order",
          "Id, AccountId, Order_Label__c, Payment_Date_new__c, Total_Amount__c, Status, CurrencyIsoCode",
          `Payment_Date_new__c >= LAST_N_MONTHS:6 AND Status='Activated' AND (NOT Account.Name LIKE 'Test %')`,
          context
        );
        console.log("Orders fetched:", orders?.length || 0);

        // Process and organize data by month
        const processedData = {};

        // Initialize months
        const today = new Date();
        for (let i = 0; i < 6; i++) {
          const monthDate = new Date(
            today.getFullYear(),
            today.getMonth() - i,
            1
          );
          const monthKey = monthDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          });
          processedData[monthKey] = {
            FrontEnd: [],
            BackEnd: [],
            PricingUpdate: [],
          };
        }

        // Categorize opportunities
        opportunities.forEach((opp) => {
          const closeDate = new Date(opp.CloseDate);
          const monthKey = closeDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          });

          if (processedData[monthKey]) {
            const oppData = {
              Opportunity: opp,
              IsPaid: false, // This would be enhanced with actual payment data
              IsProrated: false,
              Priority: getPriorityLevel(opp),
              Age: getOpportunityAge(opp.CloseDate),
              TotalValue:
                convertToUSD(opp.Total_MRR__c || 0, opp.CurrencyIsoCode) +
                convertToUSD(opp.Total_One_Offs__c || 0, opp.CurrencyIsoCode),
              OriginalMRR: opp.Total_MRR__c || 0,
              OriginalOneOff: opp.Total_One_Offs__c || 0,
              CurrencyCode: opp.CurrencyIsoCode || "USD",
            };

            // Categorize based on record type
            const recordType = opp.RecordType?.Name || "";
            const frontEndTypes = [
              "Lead Sale",
              "Reactivation",
              "Additional Office",
              "Director Referral",
              "VP Referral",
            ];
            const backEndTypes = ["Up Sell", "Product Activation"];

            if (frontEndTypes.includes(recordType)) {
              processedData[monthKey].FrontEnd.push(oppData);
            } else if (backEndTypes.includes(recordType)) {
              processedData[monthKey].BackEnd.push(oppData);
            } else {
              processedData[monthKey].PricingUpdate.push(oppData);
            }
          }
        });

        console.log("Data processing completed successfully");
        setData(processedData);
      } catch (err) {
        console.error("Error fetching data:", err);
        // Set empty data structure to prevent infinite loading
        setData({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [context]);

  if (!context?.ready) return <Loader />;
  if (loading) return <Loader />;

  const currentMonth = new Date().toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const lastMonthDate = new Date();
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
  const lastMonth = lastMonthDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const lastMonthData = data[lastMonth] || {
    FrontEnd: [],
    BackEnd: [],
    PricingUpdate: [],
  };
  const currentMonthData = data[currentMonth] || {
    FrontEnd: [],
    BackEnd: [],
    PricingUpdate: [],
  };

  const lastMonthCancellations = cancellations[lastMonth] || {
    Cancellations: [],
  };
  const currentMonthCancellations = cancellations[currentMonth] || {
    Cancellations: [],
  };

  const lastMonthRefunds = refunds[lastMonth] || {
    Transactions: [],
  };
  const currentMonthRefunds = refunds[currentMonth] || {
    Transactions: [],
  };

  const lastMonthCredits = credits[lastMonth] || {
    Transactions: [],
  };
  const currentMonthCredits = credits[currentMonth] || {
    Transactions: [],
  };

  balanceRows(lastMonthData.FrontEnd, currentMonthData.FrontEnd);
  balanceRows(lastMonthData.BackEnd, currentMonthData.BackEnd);
  balanceRows(lastMonthData.PricingUpdate, currentMonthData.PricingUpdate);

  // Enhanced analytics for management decision making
  const getAllOpportunities = () =>
    [
      ...lastMonthData.FrontEnd,
      ...lastMonthData.BackEnd,
      ...lastMonthData.PricingUpdate,
      ...currentMonthData.FrontEnd,
      ...currentMonthData.BackEnd,
      ...currentMonthData.PricingUpdate,
    ].filter(
      (item) =>
        item &&
        item.Opportunity &&
        item.Opportunity.Name &&
        item.Opportunity.Name.trim() !== ""
    );

  const getActionDashboard = () => {
    const allOpps = getAllOpportunities().filter(
      (item) => item && item.Opportunity
    );
    const urgent = allOpps.filter((item) => {
      const priority = item.Priority || getPriorityLevel(item.Opportunity);
      const age = item.Age || getOpportunityAge(item.Opportunity.CloseDate);
      return !item.IsPaid && priority === "high" && age > 30;
    });
    const highValue = allOpps.filter((item) => {
      const priority = item.Priority || getPriorityLevel(item.Opportunity);
      return priority === "high";
    });
    const longOutstanding = allOpps.filter((item) => {
      const age = item.Age || getOpportunityAge(item.Opportunity.CloseDate);
      return !item.IsPaid && age > 60;
    });

    const totalPipeline = allOpps.reduce(
      (sum, item) => sum + (item.TotalValue || 0),
      0
    );
    const atRiskValue = urgent.reduce(
      (sum, item) => sum + (item.TotalValue || 0),
      0
    );

    return { urgent, highValue, longOutstanding, totalPipeline, atRiskValue };
  };

  const actionData = getActionDashboard();

  const renderSummary = (
    monthData,
    monthCancellations,
    monthRefunds,
    monthCredits
  ) => {
    // Filter out empty/placeholder rows from all data
    const allOpportunities = [
      ...monthData.FrontEnd,
      ...monthData.BackEnd,
      ...monthData.PricingUpdate,
    ].filter(
      (item) =>
        item &&
        item.Opportunity &&
        item.Opportunity.Name &&
        item.Opportunity.Name.trim() !== ""
    );

    const totalMRR = allOpportunities.reduce(
      (sum, item) => sum + (item.Opportunity.Total_MRR__c || 0),
      0
    );
    const totalOneOffs = allOpportunities.reduce(
      (sum, item) => sum + (item.Opportunity.Total_One_Offs__c || 0),
      0
    );

    // Enhanced analytics
    const highValueCount = allOpportunities.filter((item) => {
      const priority =
        item.Priority || getPriorityLevel(item.Opportunity || {});
      return priority === "high";
    }).length;
    const urgentCount = allOpportunities.filter((item) => {
      const priority =
        item.Priority || getPriorityLevel(item.Opportunity || {});
      const age = item.Age || getOpportunityAge(item.Opportunity?.CloseDate);
      return !item.IsPaid && priority === "high" && age > 30;
    }).length;
    const averageValue =
      allOpportunities.length > 0
        ? (totalMRR + totalOneOffs) / allOpportunities.length
        : 0;

    return (
      <div className="enhanced-card">
        <h3>📊 Monthly Analytics</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">Total Revenue</span>
            <span className="stat-value main-revenue">
              {formatCurrency(totalMRR + totalOneOffs)}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">MRR</span>
            <span className="stat-value">{formatCurrency(totalMRR)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">One-time</span>
            <span className="stat-value">{formatCurrency(totalOneOffs)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Avg Deal Size</span>
            <span className="stat-value">{formatCurrency(averageValue)}</span>
          </div>
        </div>

        <div className="priority-indicators">
          <div className="priority-item high">
            <span className="priority-icon">💎</span>
            <span className="priority-text">
              {highValueCount} High-Value Deals
            </span>
          </div>
          {urgentCount > 0 && (
            <div className="priority-item urgent">
              <span className="priority-icon">🔴</span>
              <span className="priority-text">
                {urgentCount} Urgent Follow-ups
              </span>
            </div>
          )}
        </div>

        <div className="breakdown">
          <div className="breakdown-item">
            <span>
              Front End:{" "}
              {
                monthData.FrontEnd.filter((item) =>
                  item?.Opportunity?.Name?.trim()
                ).length
              }
            </span>
            <span>
              {formatCurrency(
                monthData.FrontEnd.filter((item) =>
                  item?.Opportunity?.Name?.trim()
                ).reduce((sum, item) => sum + (item.TotalValue || 0), 0)
              )}
            </span>
          </div>
          <div className="breakdown-item">
            <span>
              Back End:{" "}
              {
                monthData.BackEnd.filter((item) =>
                  item?.Opportunity?.Name?.trim()
                ).length
              }
            </span>
            <span>
              {formatCurrency(
                monthData.BackEnd.filter((item) =>
                  item?.Opportunity?.Name?.trim()
                ).reduce((sum, item) => sum + (item.TotalValue || 0), 0)
              )}
            </span>
          </div>
          <div className="breakdown-item">
            <span>
              Pricing:{" "}
              {
                monthData.PricingUpdate.filter((item) =>
                  item?.Opportunity?.Name?.trim()
                ).length
              }
            </span>
            <span>
              {formatCurrency(
                monthData.PricingUpdate.filter((item) =>
                  item?.Opportunity?.Name?.trim()
                ).reduce((sum, item) => sum + (item.TotalValue || 0), 0)
              )}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderTable = (data, title) => {
    if (!data || data.length === 0) {
      return (
        <div className="table-section">
          <h4>{title}</h4>
          <p className="no-data">No data available</p>
        </div>
      );
    }

    // Filter out empty/placeholder rows created by balanceRows
    const realData = data.filter(
      (item) =>
        item &&
        item.Opportunity &&
        item.Opportunity.Name &&
        item.Opportunity.Name.trim() !== ""
    );

    if (realData.length === 0) {
      return (
        <div className="table-section">
          <h4>{title}</h4>
          <p className="no-data">No data available</p>
        </div>
      );
    }

    // Sort data based on selected criteria - prioritize high-value deals first
    const sortedData = [...realData].sort((a, b) => {
      const aAmount = a.TotalValue || 0;
      const bAmount = b.TotalValue || 0;

      switch (sortBy) {
        case "amount":
          // First, prioritize deals over $1k, then sort by amount
          if (aAmount >= 1000 && bAmount < 1000) return -1;
          if (bAmount >= 1000 && aAmount < 1000) return 1;
          return bAmount - aAmount;
        case "date":
          return (
            new Date(b.Opportunity.CloseDate) -
            new Date(a.Opportunity.CloseDate)
          );
        case "type":
          return (
            a.Opportunity.RecordType?.Name?.localeCompare(
              b.Opportunity.RecordType?.Name
            ) || 0
          );
        default:
          // Default sort: prioritize $1k+ deals, then by amount
          if (aAmount >= 1000 && bAmount < 1000) return -1;
          if (bAmount >= 1000 && aAmount < 1000) return 1;
          return bAmount - aAmount;
      }
    });

    // Filter data based on selected filter
    const filteredData = sortedData.filter((item) => {
      if (!item || !item.Opportunity) return false;
      const priority = item.Priority || getPriorityLevel(item.Opportunity);
      const age = item.Age || getOpportunityAge(item.Opportunity.CloseDate);

      switch (selectedFilter) {
        case "high-value":
          return priority === "high";
        case "at-risk":
          return !item.IsPaid && age > 30;
        case "new":
          return age <= 7;
        default:
          return true;
      }
    });

    return (
      <div className="table-section">
        <div className="table-header">
          <h4>
            {title} ({filteredData.length} items)
          </h4>
          <div className="table-controls">
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Items</option>
              <option value="high-value">High Value</option>
              <option value="at-risk">At Risk</option>
              <option value="new">New (7 days)</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="amount">High-Value First ($1k+)</option>
              <option value="date">Sort by Date</option>
              <option value="type">Sort by Type</option>
            </select>
          </div>
        </div>

        <table className="enhanced-table">
          <thead>
            <tr>
              <th>Value</th>
              <th>Opportunity</th>
              <th>Details</th>
              <th>Financial</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item, index) => {
              const priority =
                item.Priority || getPriorityLevel(item.Opportunity || {});
              return (
                <tr
                  key={item.Opportunity?.Id || index}
                  style={{ backgroundColor: getStatusColor(item) }}
                  className={`priority-${priority}`}
                >
                  <td className="value-cell">
                    <div className="status-indicator">
                      <span
                        className="visual-indicator"
                        title={getVisualIndicatorTooltip(item)}
                      >
                        {getVisualIndicator(item)}
                      </span>
                      <span
                        className={`priority-badge priority-badge-${priority}`}
                      >
                        {priority.toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td className="opportunity-cell">
                    <a
                      href={`https://gargleinc.lightning.force.com/${item.Opportunity?.Id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opportunity-link"
                    >
                      <strong>
                        {item.Opportunity?.Name || "Unnamed Opportunity"}
                      </strong>
                    </a>
                    <div className="opportunity-meta">
                      <span className="record-type">
                        {item.Opportunity?.RecordType?.Name || "Unknown"}
                      </span>
                    </div>
                  </td>
                  <td className="details-cell">
                    <div className="detail-item">
                      <span className="detail-label">Close Date:</span>
                      <span className="detail-value">
                        {item.Opportunity?.CloseDate || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Age:</span>
                      <span className="detail-value">
                        {item.Age ||
                          getOpportunityAge(item.Opportunity?.CloseDate)}{" "}
                        days
                      </span>
                    </div>
                  </td>
                  <td className="financial-cell">
                    <div className="financial-breakdown">
                      <div className="financial-item">
                        <span className="financial-label">MRR:</span>
                        <span className="financial-value">
                          {item.CurrencyCode !== "USD"
                            ? `${formatCurrency(
                                convertToUSD(
                                  item.OriginalMRR,
                                  item.CurrencyCode
                                )
                              )} (${formatCurrency(
                                item.OriginalMRR,
                                item.CurrencyCode
                              )})`
                            : formatCurrency(item.OriginalMRR)}
                        </span>
                      </div>
                      <div className="financial-item">
                        <span className="financial-label">One-time:</span>
                        <span className="financial-value">
                          {item.CurrencyCode !== "USD"
                            ? `${formatCurrency(
                                convertToUSD(
                                  item.OriginalOneOff,
                                  item.CurrencyCode
                                )
                              )} (${formatCurrency(
                                item.OriginalOneOff,
                                item.CurrencyCode
                              )})`
                            : formatCurrency(item.OriginalOneOff)}
                        </span>
                      </div>
                      <div className="financial-total">
                        <span className="financial-label">Total:</span>
                        <span className="financial-value total">
                          {item.CurrencyCode !== "USD"
                            ? `${formatCurrency(
                                item.TotalValue
                              )} (${formatCurrency(
                                item.OriginalMRR + item.OriginalOneOff,
                                item.CurrencyCode
                              )})`
                            : formatCurrency(item.TotalValue)}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {error && <ErrorMessage />}
      {!error && (
        <>
          {!sidebar && <Sidebar />}
          <div className={owId ? "contentWOS dashboard" : "content dashboard"}>
            <div style={{ height: "100%", minHeight: "100%" }}>
              <div className="chart-container">
                <h1>📈 Profitability Dashboard</h1>

                {/* Month Progress Indicator */}
                {(() => {
                  const progressData = getMonthProgress();
                  return (
                    <div
                      className={`month-progress-indicator ${
                        progressData.isOnTrack ? "on-track" : "behind"
                      }`}
                    >
                      <div className="progress-header">
                        <span
                          className={`progress-icon ${
                            progressData.isOnTrack ? "green" : "red"
                          }`}
                        >
                          {progressData.isOnTrack ? "🟢" : "🔴"}
                        </span>
                        <span className="progress-title">
                          {progressData.isOnTrack ? "On Track" : "Behind Pace"}{" "}
                          for {currentMonth}
                        </span>
                      </div>
                      <div className="progress-details">
                        <div className="progress-stats">
                          <span className="current-vs-last">
                            Current:{" "}
                            {formatCurrency(progressData.currentMonthRevenue)}{" "}
                            vs Last Month:{" "}
                            {formatCurrency(progressData.lastMonthRevenue)}
                          </span>
                          <span className="projection">
                            Projected:{" "}
                            {formatCurrency(progressData.projectedRevenue)}(
                            {progressData.isOnTrack ? "+" : ""}
                            {formatCurrency(
                              progressData.projectedRevenue -
                                progressData.lastMonthRevenue
                            )}{" "}
                            vs last month)
                          </span>
                        </div>
                        <div className="progress-bar-container">
                          <div className="progress-bar">
                            <div
                              className={`progress-fill ${
                                progressData.isOnTrack ? "green" : "red"
                              }`}
                              style={{
                                width: `${progressData.monthProgress}%`,
                              }}
                            ></div>
                          </div>
                          <span className="progress-text">
                            Day {progressData.currentDayOfMonth} of{" "}
                            {progressData.daysInCurrentMonth} (
                            {progressData.monthProgress.toFixed(1)}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Deal Value Legend */}
                <div className="deal-value-legend">
                  <h3>🏆 Deal Value Recognition</h3>
                  <div className="legend-items">
                    <div className="legend-item">
                      <span className="legend-icon">🏆</span>
                      <span className="legend-text">$2k+ Legendary!!</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-icon">🤩</span>
                      <span className="legend-text">$1k+ Amazing</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-icon">😎</span>
                      <span className="legend-text">$500+ Awesome</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-icon">👏</span>
                      <span className="legend-text">$200+ Well Done</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-icon">👍</span>
                      <span className="legend-text">Standard</span>
                    </div>
                  </div>
                </div>

                {/* Executive Action Dashboard */}
                <div className="executive-dashboard">
                  <div className="action-cards">
                    <div className="action-card urgent">
                      <div className="action-header">
                        <span className="action-icon">🔴</span>
                        <h3>Urgent Actions</h3>
                      </div>
                      <div className="action-metric">
                        {actionData.urgent.length}
                      </div>
                      <div className="action-description">
                        High-value deals &gt;30 days old
                      </div>
                      <div className="action-value">
                        Value at Risk: {formatCurrency(actionData.atRiskValue)}
                      </div>
                    </div>

                    <div className="action-card high-value">
                      <div className="action-header">
                        <span className="action-icon">💎</span>
                        <h3>High-Value Pipeline</h3>
                      </div>
                      <div className="action-metric">
                        {actionData.highValue.length}
                      </div>
                      <div className="action-description">
                        Deals requiring attention
                      </div>
                      <div className="action-value">
                        Total Value:{" "}
                        {formatCurrency(
                          actionData.highValue.reduce(
                            (sum, item) => sum + item.TotalValue,
                            0
                          )
                        )}
                      </div>
                    </div>

                    <div className="action-card long-outstanding">
                      <div className="action-header">
                        <span className="action-icon">⏰</span>
                        <h3>Long Outstanding</h3>
                      </div>
                      <div className="action-metric">
                        {actionData.longOutstanding.length}
                      </div>
                      <div className="action-description">
                        Deals &gt;60 days old
                      </div>
                      <div className="action-value">Needs Investigation</div>
                    </div>

                    <div className="action-card pipeline">
                      <div className="action-header">
                        <span className="action-icon">💰</span>
                        <h3>Total Pipeline</h3>
                      </div>
                      <div className="action-metric">
                        {formatCurrency(actionData.totalPipeline)}
                      </div>
                      <div className="action-description">
                        2-month pipeline value
                      </div>
                      <div className="action-value">
                        Conversion Rate:{" "}
                        {getAllOpportunities().length > 0
                          ? (
                              (getAllOpportunities().filter(
                                (item) => item.IsPaid
                              ).length /
                                getAllOpportunities().length) *
                              100
                            ).toFixed(1)
                          : 0}
                        %
                      </div>
                    </div>
                  </div>
                </div>

                <div className="month-comparison-container">
                  <div className="month-column">
                    <h2>{lastMonth}</h2>
                    {renderSummary(
                      lastMonthData,
                      lastMonthCancellations,
                      lastMonthRefunds,
                      lastMonthCredits
                    )}
                    <h3>
                      {formatCurrency(
                        [
                          ...lastMonthData.FrontEnd,
                          ...lastMonthData.BackEnd,
                          ...lastMonthData.PricingUpdate,
                        ]
                          .filter((item) => item?.Opportunity?.Name?.trim())
                          .reduce(
                            (sum, item) =>
                              sum + (item.Opportunity.Total_MRR__c || 0),
                            0
                          )
                      )}
                      <span style={{ marginLeft: "10px", color: "blue" }}>
                        (One-Offs:{" "}
                        {formatCurrency(
                          [
                            ...lastMonthData.FrontEnd,
                            ...lastMonthData.BackEnd,
                            ...lastMonthData.PricingUpdate,
                          ]
                            .filter((item) => item?.Opportunity?.Name?.trim())
                            .reduce(
                              (sum, item) =>
                                sum + (item.Opportunity.Total_One_Offs__c || 0),
                              0
                            )
                        )}
                        )
                      </span>
                    </h3>
                    <hr />
                    {renderTable(lastMonthData.FrontEnd, "Front End")}
                    <hr />
                    {renderTable(lastMonthData.BackEnd, "Back End")}
                    <hr />
                    {renderTable(lastMonthData.PricingUpdate, "Pricing Update")}
                  </div>
                  <div className="month-column">
                    <h2>{currentMonth}</h2>
                    {renderSummary(
                      currentMonthData,
                      currentMonthCancellations,
                      currentMonthRefunds,
                      currentMonthCredits
                    )}
                    <h3>
                      {formatCurrency(
                        [
                          ...currentMonthData.FrontEnd,
                          ...currentMonthData.BackEnd,
                          ...currentMonthData.PricingUpdate,
                        ]
                          .filter((item) => item?.Opportunity?.Name?.trim())
                          .reduce(
                            (sum, item) =>
                              sum + (item.Opportunity.Total_MRR__c || 0),
                            0
                          )
                      )}
                      <span style={{ marginLeft: "10px", color: "blue" }}>
                        (One-Offs:{" "}
                        {formatCurrency(
                          [
                            ...currentMonthData.FrontEnd,
                            ...currentMonthData.BackEnd,
                            ...currentMonthData.PricingUpdate,
                          ]
                            .filter((item) => item?.Opportunity?.Name?.trim())
                            .reduce(
                              (sum, item) =>
                                sum + (item.Opportunity.Total_One_Offs__c || 0),
                              0
                            )
                        )}
                        )
                      </span>
                    </h3>
                    <hr />
                    {renderTable(currentMonthData.FrontEnd, "Front End")}
                    <hr />
                    {renderTable(currentMonthData.BackEnd, "Back End")}
                    <hr />
                    {renderTable(
                      currentMonthData.PricingUpdate,
                      "Pricing Update"
                    )}
                  </div>
                </div>
                <button onClick={() => setShowOlder(!showOlder)}>
                  {showOlder ? "Hide Older Data" : "Show Older Data"}
                </button>
                {showOlder && (
                  <div>
                    {Object.entries(data)
                      .filter(
                        ([month]) =>
                          month !== currentMonth && month !== lastMonth
                      )
                      .map(([month, groupData]) => (
                        <div key={month}>
                          <h2>{month}</h2>
                          {renderTable(groupData.FrontEnd, "Front End")}
                          {renderTable(groupData.BackEnd, "Back End")}
                          {renderTable(
                            groupData.PricingUpdate,
                            "Pricing Update"
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      <style jsx>{`
        .content {
          padding: 20px;
          max-width: 100vw; /* Prevent horizontal overflow */
          overflow-x: hidden; /* Hide any overflow */
          box-sizing: border-box;
        }

        .dashboard {
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
        }

        .chart-container {
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
        }

        /* Month Progress Indicator */
        .month-progress-indicator {
          margin: 20px 0 30px 0;
          padding: 20px;
          border-radius: 12px;
          border: 2px solid;
          background: white;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        }

        .month-progress-indicator.on-track {
          border-color: #10b981;
          background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
        }

        .month-progress-indicator.behind {
          border-color: #ef4444;
          background: linear-gradient(135deg, #fef2f2 0%, #fef2f2 100%);
        }

        .progress-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 15px;
        }

        .progress-icon {
          font-size: 1.5rem;
        }

        .progress-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: #1f2937;
        }

        .progress-details {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .progress-stats {
          display: flex;
          flex-direction: column;
          gap: 6px;
          font-size: 0.9rem;
        }

        .current-vs-last {
          color: #374151;
          font-weight: 500;
        }

        .projection {
          color: #6b7280;
        }

        .progress-bar-container {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .progress-bar {
          flex: 1;
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          transition: width 0.3s ease;
        }

        .progress-fill.green {
          background: linear-gradient(90deg, #10b981, #059669);
        }

        .progress-fill.red {
          background: linear-gradient(90deg, #ef4444, #dc2626);
        }

        .progress-text {
          font-size: 0.85rem;
          color: #6b7280;
          font-weight: 500;
        }

        /* Deal Value Legend */
        .deal-value-legend {
          margin-bottom: 25px;
          padding: 20px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          border: 1px solid #e5e7eb;
        }

        .deal-value-legend h3 {
          margin: 0 0 15px 0;
          color: #1f2937;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .legend-items {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 10px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .legend-icon {
          font-size: 1.2rem;
        }

        .legend-text {
          font-size: 0.85rem;
          font-weight: 500;
          color: #374151;
        }

        /* Month Comparison Layout - Enhanced for Desktop/Mobile */
        .month-comparison-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          margin-bottom: 30px;
          min-height: 500px; /* Ensures consistent height */
        }

        .month-column {
          background: white;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          border: 1px solid #e5e7eb;
          overflow: hidden; /* Prevents content overflow */
          display: flex;
          flex-direction: column;
        }

        .month-column h2 {
          margin: 0 0 20px 0;
          color: #1f2937;
          font-size: 1.5rem;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          padding-bottom: 10px;
          text-align: center;
        }

        .month-column h3 {
          margin: 15px 0;
          font-size: 1.2rem;
          color: #374151;
          text-align: center;
          background: #f8fafc;
          padding: 10px;
          border-radius: 8px;
        }

        .month-column hr {
          margin: 20px 0;
          border: none;
          border-top: 1px solid #e5e7eb;
        }

        /* Executive Dashboard Styles */
        .executive-dashboard {
          margin-bottom: 30px;
          padding: 25px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 15px;
          color: white;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .executive-dashboard h2 {
          margin: 0 0 20px 0;
          font-size: 1.8rem;
          font-weight: 600;
        }

        .action-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
        }

        .action-card {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .action-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .action-card.urgent {
          border-left: 4px solid #ff6b6b;
        }

        .action-card.high-value {
          border-left: 4px solid #ffd93d;
        }

        .action-card.long-outstanding {
          border-left: 4px solid #ff9500;
        }

        .action-card.pipeline {
          border-left: 4px solid #6bcf7f;
        }

        .action-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 15px;
        }

        .action-icon {
          font-size: 1.5rem;
        }

        .action-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .action-metric {
          font-size: 2.2rem;
          font-weight: bold;
          margin-bottom: 8px;
        }

        .action-description {
          font-size: 0.9rem;
          opacity: 0.9;
          margin-bottom: 8px;
        }

        .action-value {
          font-size: 0.85rem;
          font-weight: 500;
          opacity: 0.8;
        }

        /* Enhanced Card Styles */
        .enhanced-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          border: 1px solid #e5e7eb;
          margin-bottom: 20px;
          box-sizing: border-box; /* Ensure padding is included in width calculations */
        }

        .enhanced-card h3 {
          margin: 0 0 20px 0;
          color: #1f2937;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 15px;
          margin-bottom: 20px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          text-align: center;
          padding: 15px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .stat-label {
          font-size: 0.8rem;
          color: #64748b;
          margin-bottom: 5px;
          font-weight: 500;
        }

        .stat-value {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1e293b;
        }

        .stat-value.main-revenue {
          font-size: 1.4rem;
          color: #059669;
        }

        .priority-indicators {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .priority-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .priority-item.high {
          background: #fef3c7;
          color: #92400e;
        }

        .priority-item.urgent {
          background: #fee2e2;
          color: #991b1b;
        }

        .breakdown {
          border-top: 1px solid #e5e7eb;
          padding-top: 15px;
        }

        .breakdown-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #f3f4f6;
          font-size: 0.9rem;
        }

        .breakdown-item:last-child {
          border-bottom: none;
        }

        /* Enhanced Table Styles */
        .table-section {
          margin-bottom: 30px;
          overflow-x: auto; /* Prevent horizontal overflow */
          background: white;
          border-radius: 8px;
          padding: 15px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          border: 1px solid #e5e7eb;
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .table-header h4 {
          margin: 0;
          color: #1f2937;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .table-controls {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .filter-select,
        .sort-select {
          padding: 6px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.85rem;
          background: white;
          cursor: pointer;
        }

        .filter-select:focus,
        .sort-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .enhanced-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          table-layout: fixed; /* Prevent table from expanding beyond container */
        }

        .enhanced-table th {
          background: #f8fafc;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          font-size: 0.85rem;
          border-bottom: 2px solid #e5e7eb;
        }

        .enhanced-table th:nth-child(1) {
          width: 15%;
        } /* Value */
        .enhanced-table th:nth-child(2) {
          width: 35%;
        } /* Opportunity */
        .enhanced-table th:nth-child(3) {
          width: 22%;
        } /* Details */
        .enhanced-table th:nth-child(4) {
          width: 28%;
        } /* Financial */

        .enhanced-table td {
          padding: 12px;
          border-bottom: 1px solid #f3f4f6;
          font-size: 0.85rem;
          vertical-align: top;
        }

        .enhanced-table tr:hover {
          background: #f9fafb;
        }

        .enhanced-table tr.priority-high {
          border-left: 3px solid #f59e0b;
        }

        .enhanced-table tr.priority-medium {
          border-left: 3px solid #3b82f6;
        }

        .enhanced-table tr.priority-low {
          border-left: 3px solid #6b7280;
        }

        .value-cell {
          width: 15%;
        }

        .status-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
        }

        .visual-indicator {
          font-size: 1.2rem;
          cursor: help;
        }

        .priority-badge {
          font-size: 0.7rem;
          padding: 2px 6px;
          border-radius: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .priority-badge-high {
          background: #fef3c7;
          color: #92400e;
        }

        .priority-badge-medium {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .priority-badge-low {
          background: #f3f4f6;
          color: #6b7280;
        }

        .opportunity-cell {
          width: 28%;
          word-wrap: break-word;
        }

        .opportunity-link {
          text-decoration: none;
          color: #3b82f6;
          font-weight: 500;
        }

        .opportunity-link:hover {
          text-decoration: underline;
        }

        .opportunity-meta {
          margin-top: 4px;
        }

        .record-type {
          font-size: 0.75rem;
          color: #6b7280;
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
        }

        .details-cell {
          width: 18%;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
        }

        .detail-label {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
        }

        .detail-value {
          font-size: 0.75rem;
          color: #374151;
        }

        .financial-cell {
          width: 22%;
        }

        .financial-breakdown {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .financial-item {
          display: flex;
          justify-content: space-between;
        }

        .financial-label {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .financial-value {
          font-size: 0.75rem;
          font-weight: 500;
          color: #374151;
        }

        .financial-total {
          display: flex;
          justify-content: space-between;
          border-top: 1px solid #e5e7eb;
          padding-top: 4px;
          margin-top: 4px;
        }

        .financial-value.total {
          font-weight: 600;
          color: #059669;
        }

        .action-cell {
          width: 20%;
        }

        .action-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .action-text {
          font-size: 0.8rem;
          color: #374151;
          font-weight: 500;
        }

        .action-buttons {
          display: flex;
          gap: 6px;
        }

        .action-btn {
          padding: 4px 8px;
          border-radius: 4px;
          border: none;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .action-btn.follow-up {
          background: #3b82f6;
          color: white;
        }

        .action-btn.follow-up:hover {
          background: #2563eb;
        }

        .no-data {
          text-align: center;
          color: #6b7280;
          font-style: italic;
          padding: 20px;
        }

        /* Desktop Layout Optimization */
        @media (min-width: 1024px) {
          .month-comparison-container {
            grid-template-columns: 1fr 1fr;
            gap: 40px;
            margin-bottom: 40px;
          }

          .month-column {
            padding: 30px;
          }

          .enhanced-table {
            font-size: 0.9rem;
          }

          .enhanced-table th,
          .enhanced-table td {
            padding: 15px 12px;
          }
        }

        /* Large Desktop Layout */
        @media (min-width: 1200px) {
          .content {
            padding: 30px;
          }

          .month-comparison-container {
            gap: 50px;
          }

          .executive-dashboard {
            padding: 30px;
          }

          .action-cards {
            grid-template-columns: repeat(4, 1fr);
            gap: 25px;
          }
        }

        /* Legacy table styles for compatibility */
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 10px 0;
        }
        th,
        td {
          border: 1px solid #ddd;
          text-align: left;
          padding: 4px;
          font-size: 11px;
        }
        th {
          background-color: #f4f4f4;
          font-weight: bold;
        }
        .card-container {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-around;
          margin-bottom: 20px;
        }
        .card {
          width: 100%;
          margin: 10px;
          padding: 15px;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
          background-color: #f9f9f9;
          text-align: left;
        }
        .card h3 {
          font-size: 14px;
          margin-bottom: 10px;
        }
        .card p {
          font-size: 12px;
          margin: 0;
          display: inline-block;
          margin-left: 15px;
        }
        .card hr {
          margin: 5px;
        }

        @media (max-width: 768px) {
          /* Month Progress Indicator - Mobile */
          .month-progress-indicator {
            margin: 15px 0 20px 0;
            padding: 15px;
          }

          .progress-header {
            margin-bottom: 12px;
          }

          .progress-title {
            font-size: 1.1rem;
          }

          .progress-stats {
            font-size: 0.8rem;
          }

          .progress-text {
            font-size: 0.75rem;
          }

          /* Deal Value Legend - Mobile */
          .deal-value-legend {
            margin-bottom: 20px;
            padding: 15px;
          }

          .deal-value-legend h3 {
            font-size: 1rem;
            margin-bottom: 12px;
          }

          .legend-items {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }

          .legend-item {
            padding: 6px 8px;
          }

          .legend-icon {
            font-size: 1rem;
          }

          .legend-text {
            font-size: 0.75rem;
          }

          /* Executive Dashboard - Mobile */
          .executive-dashboard {
            padding: 15px;
            margin-bottom: 20px;
          }

          .action-cards {
            grid-template-columns: 1fr;
            gap: 15px;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .month-comparison-container {
            grid-template-columns: 1fr; /* Stack columns vertically on mobile */
            gap: 20px;
            min-height: auto;
          }

          .month-column {
            width: 100%;
            padding: 20px;
            margin: 0;
          }

          .month-column h2 {
            font-size: 1.3rem;
          }

          .month-column h3 {
            font-size: 1rem;
            padding: 8px;
          }

          .table-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .table-controls {
            width: 100%;
            justify-content: flex-start;
          }

          .enhanced-table {
            font-size: 0.8rem;
            table-layout: auto; /* Allow table to be flexible on mobile */
            overflow-x: auto; /* Enable horizontal scrolling if needed */
          }

          .enhanced-table th,
          .enhanced-table td {
            padding: 8px 6px;
            min-width: 80px; /* Ensure minimum readable width */
          }

          /* Reset fixed column widths on mobile for better flexibility */
          .enhanced-table th:nth-child(1),
          .enhanced-table th:nth-child(2),
          .enhanced-table th:nth-child(3),
          .enhanced-table th:nth-child(4) {
            width: auto;
          }

          .value-cell,
          .opportunity-cell,
          .details-cell,
          .financial-cell {
            width: auto;
            min-width: 80px;
          }

          .opportunity-cell {
            min-width: 120px; /* Wider for opportunity names */
          }

          .financial-cell {
            min-width: 100px; /* Wider for financial data */
          }

          /* Legacy compatibility */
          .last-month-data {
            display: none;
          }

          .notes-column {
            width: 20%;
          }

          .opportunity-name-column {
            flex: 1;
          }

          .card-container {
            flex-direction: column;
            align-items: center;
          }

          .card {
            width: 90%;
            margin: 5px;
            padding: 12px;
          }

          table {
            font-size: 10px;
          }

          th,
          td {
            padding: 3px;
          }
        }

        @media (max-width: 480px) {
          /* Month Progress Indicator - Small Mobile */
          .month-progress-indicator {
            margin: 10px 0 15px 0;
            padding: 12px;
          }

          .progress-title {
            font-size: 1rem;
          }

          .progress-stats {
            font-size: 0.75rem;
          }

          .progress-bar-container {
            flex-direction: column;
            gap: 8px;
          }

          .progress-text {
            font-size: 0.7rem;
            text-align: center;
          }

          /* Deal Value Legend - Small Mobile */
          .deal-value-legend {
            margin-bottom: 15px;
            padding: 12px;
          }

          .deal-value-legend h3 {
            font-size: 0.9rem;
            margin-bottom: 10px;
          }

          .legend-items {
            grid-template-columns: 1fr;
            gap: 6px;
          }

          .legend-item {
            padding: 5px 8px;
          }

          .legend-text {
            font-size: 0.7rem;
          }

          /* Executive Dashboard - Small Mobile */
          .executive-dashboard {
            margin-bottom: 20px;
            padding: 12px;
          }

          .action-metric {
            font-size: 1.8rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .month-comparison-container {
            gap: 20px;
          }

          .content {
            padding: 10px;
          }

          .card {
            padding: 10px;
          }

          th,
          td {
            font-size: 9px;
            padding: 2px;
          }

          h1 {
            font-size: 20px;
          }

          h2 {
            font-size: 16px;
          }
        }
      `}</style>
    </>
  );
};

export default ProfitabilityDashboard;
